/* @flow */
import * as React from "react";
import * as ReactDOM from "react-dom";
import { connect } from "react-redux";
import { createSelector } from "reselect";
import identity from "lodash/identity";
import pick from "lodash/pick";
import omit from "lodash/omit";
import zipObject from "lodash/zipObject";
import Spinner from "./Spinners/BrandSpinner";
import SimpleSpinner from "./Spinners/SimpleSpinner";

import type { State } from "../redux/reducer";
import type { Dispatch } from "../redux/action";

const updateActionMap = {
  profile: { type: "REQUEST_PROFILE" },
  accounts: { type: "REQUEST_ACCOUNTS" },
  remoteTradeSettings: { type: "REQUEST_TRADE_SETTINGS" },
  serverData: { type: "REQUEST_SERVER_DATA" },
  deals: { type: "REQUEST_DEAL_HISTORY" },
  apiConfigs: { type: "REQUEST_API_CONFIGS" },
  apiCountries: { type: "REQUEST_API_COUNTRIES" },
  stocks: { type: "REQUEST_STOCK_ASSETS" },
  isIOSShowDeposit: { type: "REQUEST_IOS_SHOW_DEPOSIT" },
  withdrawOutputMethods: { type: "REQUEST_WITHDRAW_OUTPUT_METHODS" }
};

type WrapperProps = {
  component: React.ComponentType<*>,
  __required: { [key: *]: string },
  __optional: { [key: *]: string },
  dispatch: Dispatch,
  spinner: "simple" | "brand" | "none" | null,
  selector: Object => *
};

class GotDataOrWhat extends React.Component<WrapperProps> {
  componentDidMount() {
    ReactDOM.unstable_batchedUpdates(() => {
      for (const key of Object.keys(this.props.__required)) {
        if (
          this.props.__required[key] === "UNAVAILABLE" &&
          updateActionMap[key]
        ) {
          this.props.dispatch(updateActionMap[key]);
        }
      }

      for (const key of Object.keys(this.props.__optional)) {
        if (
          this.props.__optional[key] === "UNAVAILABLE" &&
          updateActionMap[key]
        ) {
          this.props.dispatch(updateActionMap[key]);
        }
      }
    });
  }

  render() {
    const {
      component,
      __required,
      __optional,
      selector,
      ...props
    } = this.props;

    // Если какое-то из обязательных полей поломано, паникуем
    Object.keys(__required).forEach(key => {
      if (__required[key] === "INVALID") {
        throw new TypeError(`Failed retrieving "${key}" data from the server`);
      }
    });

    if (
      Object.keys(__required).some(
        key => __required[key] !== "AVAILABLE" && props[key] == null
      )
    ) {
      switch (this.props.spinner) {
        case "simple":
          return <SimpleSpinner accent={true} />;
        case "none":
          return null;
        default:
          return <Spinner />;
      }
    }

    const Component = component;

    const dataKeys = [...Object.keys(__required), ...Object.keys(__optional)];

    const dataProps = pick(props, dataKeys);
    const nonDataProps = omit(props, dataKeys);

    return (
      <Component {...nonDataProps} {...selector(dataProps, nonDataProps)} />
    );
  }
}

type Options<SelectedProps = {}> =
  | Array<string>
  | {
      required?: Array<$Keys<State>>,
      optional?: Array<$Keys<State>>,
      selector?: ({ [key: $Keys<State>]: * }) => SelectedProps,
      spinner?: "simple" | "brand" | "none"
    };

function withData<SelectedProps>(
  options: Options<SelectedProps>,
  customSelector?: Object => SelectedProps
) {
  const required = Array.isArray(options) ? options : options.required || [];
  const optional = Array.isArray(options) ? [] : options.optional || [];
  const spinner = Array.isArray(options) ? null : options.spinner || null;
  const allFields = [...required, ...optional];

  const selector =
    (Array.isArray(options) ? customSelector : options.selector) || identity;

  const mapStateToProps = createSelector(
    [
      (state: State) => state.dataRequirementsStatus,
      ...allFields.map(field => (state: State) => state[field])
    ],
    (statuses, ...data) => ({
      __required: pick(statuses, required),
      __optional: pick(statuses, optional),
      ...zipObject(allFields, data)
    })
  );

  const Wrapper = connect(mapStateToProps)(GotDataOrWhat);

  return function<T>(Component: React.ComponentType<T & SelectedProps>) {
    return (props: T) => (
      <Wrapper
        {...props}
        component={Component}
        spinner={spinner}
        selector={selector}
      />
    );
  };
}

export default withData;



// WEBPACK FOOTER //
// ./src/components/withData.js